import { Box, Heading, SubPageLayout, TextBlock } from "@bcc-ui/ui-components";
import { useFeature } from "flagged";
import { t } from "i18next";
import React from "react";

import style from "./report-customization-form.scss";
import ImportLogo from "components/settings/import-logo/ImportLogo";
import AddReportSignatureKeyDialog from "components/settings/report-signature-keys/AddReportSignatureKeyDialog";
import ReportSignatureKeyResultModal from "components/settings/report-signature-keys/ReportSignatureKeyResultModal";
import ReportSignatureKeysTable from "components/settings/report-signature-keys/ReportSignatureKeysTable";
import { useSignatureKeyActionResult } from "components/settings/report-signature-keys/useSignatureKeyActionResult";
import { AUTH_SIGNATURE_KEY_CREATE, AUTH_SIGNATURE_KEY_VIEW, AUTH_TENANT_SETTINGS } from "domain/authority";
import { ReportSignatureKey } from "domain/reportSignatureKeys";
import { FLAG_REPORT_SIGNATURE_KEYS } from "services/feature/FeatureFlagService";
import { reportSignatureKeyService } from "services/report/erasure/ReportSignatureKeyService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import { tenantService } from "services/tenants/TenantService";
import { userSessionService } from "services/user/UserSessionService";

export const ReportCustomizationForm = () => {
    const isReportSignatureKeysFeatureEnabled = useFeature(FLAG_REPORT_SIGNATURE_KEYS);
    const hasCustomReportSignatureKeysViewAuth =
        isReportSignatureKeysFeatureEnabled && userSessionService.userHasAllAuthorities([AUTH_SIGNATURE_KEY_VIEW]);
    const [counter, setCounter] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(hasCustomReportSignatureKeysViewAuth);
    const [keys, setKeys] = React.useState<ReportSignatureKey[]>([]);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState<string | undefined>(undefined);

    const onChange = () => setCounter((prevCount) => prevCount + 1);
    const { result, showResult, hideResult } = useSignatureKeyActionResult(onChange);

    React.useEffect(() => {
        if (!hasCustomReportSignatureKeysViewAuth) {
            return;
        }
        const abortController = new AbortController();
        setIsLoading(true);
        reportSignatureKeyService
            .fetchKeys(abortController)
            .then((fetchedKeys) => {
                setKeys(fetchedKeys);
                setRequestFailureMessage(undefined);
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    setRequestFailureMessage(t("ReportSignatureKeys.table.requestFailed"));
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setIsLoading(false);
                }
            });
        return () => abortController.abort();
    }, [counter, hasCustomReportSignatureKeysViewAuth]);

    return (
        <>
            <SubPageLayout
                loading={isLoading}
                hideCancelButton
                hideBackArrow
                padding="None"
                className={style.subPageLayout}
                hideFooter
            >
                <Box columnLayout paddingTop="Md" paddingBottom="Md" gap="Lg" className={style.formContent}>
                    {userSessionService.userHasAllAuthorities([AUTH_TENANT_SETTINGS]) && (
                        <Box columnLayout>
                            <Heading variant="h2" underline>
                                {t("Settings.selectFile.title")}
                            </Heading>

                            <ImportLogo fileList={undefined} tenantService={tenantService} />
                        </Box>
                    )}

                    {hasCustomReportSignatureKeysViewAuth && (
                        <Box columnLayout>
                            <Heading variant="h2" underline>
                                {t("ReportSignatureKeys.title")}
                            </Heading>

                            <TextBlock>{t("ReportSignatureKeys.description")}</TextBlock>

                            <ReportSignatureKeysTable
                                keys={keys}
                                failureMessage={requestFailureMessage}
                                onKeyChange={onChange}
                                showResult={showResult}
                            />

                            {!hasTenantCookie() &&
                                userSessionService.userHasAllAuthorities([AUTH_SIGNATURE_KEY_CREATE]) && (
                                    <Box paddingTop={"Xs"}>
                                        <AddReportSignatureKeyDialog
                                            existingNames={keys.map((key) => key.name)}
                                            showResult={showResult}
                                        />
                                    </Box>
                                )}
                        </Box>
                    )}
                </Box>
            </SubPageLayout>

            <ReportSignatureKeyResultModal result={result} onClose={hideResult} />
        </>
    );
};
